'use client';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import type { EventPlayer } from '../../libs/eventPlayers';
import { eventPlayerHref } from '../../libs/eventPlayers';
import type { JourneyFixture } from '../../libs/playerJourney';
import { displayMatchCode } from '../../libs/matchCode';

export type JourneyContext = { eventId: string; slug: string; locale: string };
export function JourneyFixtureCard({
  fixture,
  opponents,
  division,
  showOpponents = true,
  ...context
}: JourneyContext & {
  fixture: JourneyFixture;
  opponents: EventPlayer[];
  division: string;
  showOpponents?: boolean;
}) {
  const t = useTranslations('Event.playerJourney');
  // A summary names only a settled opponent; the full path owns feeder groups.
  const candidates = (fixture.opponentIds.length === 1 ? fixture.opponentIds : [])
    .map((id) => opponents.find((p) => p.playerId === id))
    .filter((p): p is EventPlayer => !!p);
  return (
    <div className="space-y-3">
      <p className="text-muted text-xs">
        {[displayMatchCode(fixture.drawMatchId), fixture.stageName, fixture.roundName].filter(Boolean).join(' · ')}
      </p>
      {showOpponents && (
        <div>
          <p className="text-muted mb-1 text-xs">
            {t(candidates.length > 1 ? 'possibleOpponents' : 'opponent')}
          </p>
          {candidates.length ? (
            <ul className="divide-border divide-y">
              {candidates.map((p) => (
                <li key={p.id}>
                  <Link
                    className="text-foreground inline-flex min-h-11 items-center font-semibold underline-offset-4 hover:underline"
                    href={`/${context.locale}${eventPlayerHref(context.eventId, context.slug, p.playerId, division)}`}
                  >
                    {p.label}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-foreground font-medium">{t('opponentPending')}</p>
          )}
        </div>
      )}
      <div className="text-muted space-y-1 text-sm">
        {fixture.scheduledAt ? (
          <LocalDateTime iso={fixture.scheduledAt} locale={context.locale} />
        ) : (
          <p>{t('unscheduled')}</p>
        )}
        <p>{fixture.court ?? t('courtPending')}</p>
      </div>
    </div>
  );
}
